// Exam admit card — one per A4 page. Combines the same student record used
// for ID cards (photo, admission no., DOB, parents) with a saved exam
// datesheet (exam name, session dates, day-by-day subject/time schedule).
import PrintSheet from '../ui/PrintSheet';
import { fmtDate, initials } from '../ui/format';

const BRAND = '#0e6e64';
const INK = '#101b22';
const SOFT = '#5b6b70';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayName = (ymd) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return '—';
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : DAY_NAMES[d.getDay()];
};

// 'HH:MM' (24h, from a <input type="time">) → '9:00 AM'
const fmtTime = (hhmm) => {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return '—';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

export function AdmitCard({ student: s, school, datesheet }) {
  const rows = [
    ['Class', s.class_name ? `${s.class_name}${s.roll_number ? ` · Roll ${s.roll_number}` : ''}` : null],
    ['Admission No.', s.admission_no],
    ['D.O.B.', s.date_of_birth ? fmtDate(s.date_of_birth) : null],
    s.father_name ? ['Father’s Name', s.father_name] : null,
    s.mother_name ? ['Mother’s Name', s.mother_name] : null,
  ].filter(Boolean).filter(([, v]) => v);

  const entries = datesheet?.entries || [];
  const session = datesheet?.start_date
    ? `${fmtDate(datesheet.start_date)}${datesheet.end_date && datesheet.end_date !== datesheet.start_date ? ` – ${fmtDate(datesheet.end_date)}` : ''}`
    : null;

  return (
    <div className="admit-card" style={{
      width: '100%', maxWidth: '190mm', margin: '0 auto', boxSizing: 'border-box', border: `0.5mm solid ${BRAND}`,
      borderRadius: '2mm', overflow: 'hidden', background: '#fff', color: INK,
      fontFamily: "'Public Sans', system-ui, sans-serif",
    }}>
      <div style={{ background: BRAND, color: '#fff', padding: '4mm 6mm 3mm', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Archivo', system-ui, sans-serif", fontWeight: 800, fontSize: '15pt', lineHeight: 1.15 }}>
          {school?.name}
        </div>
        {school?.location && <div style={{ fontSize: '9pt', opacity: 0.9, marginTop: '0.8mm' }}>{school.location}</div>}
      </div>
      <div style={{ background: '#e3f1ee', color: BRAND, fontSize: '10pt', fontWeight: 800, letterSpacing: '0.12em',
        textAlign: 'center', padding: '2mm 0' }}>
        ADMIT CARD
      </div>

      <div style={{ textAlign: 'center', padding: '3mm 4mm 0' }}>
        <div style={{ fontSize: '11pt', fontWeight: 700, color: BRAND }}>{datesheet?.exam_name}</div>
        {session && <div style={{ fontSize: '8.5pt', color: SOFT, marginTop: '0.6mm' }}>{session}</div>}
      </div>

      <div style={{ display: 'flex', gap: '5mm', alignItems: 'flex-start', padding: '4mm 6mm 0' }}>
        {s.photo ? (
          <img src={s.photo} alt="" style={{ width: '24mm', height: '29mm', objectFit: 'cover', borderRadius: '1.5mm',
            border: `0.4mm solid ${BRAND}`, flexShrink: 0 }} />
        ) : (
          <div style={{ width: '24mm', height: '29mm', borderRadius: '1.5mm', border: `0.4mm dashed ${SOFT}`,
            display: 'grid', placeItems: 'center', fontSize: '14pt', fontWeight: 800, color: SOFT, flexShrink: 0 }}>
            {initials(s.name)}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Archivo', system-ui, sans-serif", fontWeight: 800, fontSize: '13pt', lineHeight: 1.2, marginBottom: '2mm' }}>
            {s.name}
          </div>
          <div style={{ fontSize: '9pt', lineHeight: 1.7, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '3mm', rowGap: '0.5mm' }}>
            {rows.map(([label, value]) => [
              <span key={`${label}-l`} style={{ color: SOFT, fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>,
              <span key={`${label}-v`} style={{ fontWeight: 600 }}>{value}</span>,
            ])}
          </div>
        </div>
      </div>

      <div style={{ margin: '5mm 6mm 0' }}>
        <div style={{ fontSize: '8.5pt', fontWeight: 800, color: BRAND, letterSpacing: '0.08em', marginBottom: '1.5mm' }}>
          EXAMINATION SCHEDULE
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt' }}>
          <thead>
            <tr>
              {['Date', 'Day', 'Subject', 'Time'].map(h => (
                <th key={h} style={{ border: `0.3mm solid ${SOFT}`, background: '#e3f1ee', color: BRAND,
                  padding: '1.5mm 2mm', textAlign: 'left', fontWeight: 800 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i}>
                <td style={{ border: `0.3mm solid #c9d3d0`, padding: '1.5mm 2mm' }}>{fmtDate(e.exam_date)}</td>
                <td style={{ border: `0.3mm solid #c9d3d0`, padding: '1.5mm 2mm' }}>{dayName(e.exam_date)}</td>
                <td style={{ border: `0.3mm solid #c9d3d0`, padding: '1.5mm 2mm', fontWeight: 700 }}>{e.subject}</td>
                <td style={{ border: `0.3mm solid #c9d3d0`, padding: '1.5mm 2mm' }}>
                  {e.start_time ? `${fmtTime(e.start_time)}${e.end_time ? ` – ${fmtTime(e.end_time)}` : ''}` : '—'}
                </td>
              </tr>
            ))}
            {!entries.length && (
              <tr><td colSpan={4} style={{ border: `0.3mm solid #c9d3d0`, padding: '2mm', color: SOFT, textAlign: 'center' }}>
                No schedule entries added yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ margin: '5mm 6mm 0', fontSize: '7.5pt', color: SOFT, lineHeight: 1.5 }}>
        Students must carry this admit card to the examination hall on every exam day. Entry without it is not permitted.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        padding: '8mm 6mm 5mm', fontSize: '8pt', color: SOFT }}>
        <span style={{ borderTop: `0.2mm solid ${INK}`, paddingTop: '0.8mm', minWidth: '30mm', textAlign: 'center' }}>Class Teacher</span>
        <span style={{ borderTop: `0.2mm solid ${INK}`, paddingTop: '0.8mm', minWidth: '30mm', textAlign: 'center' }}>Principal</span>
      </div>
      <div style={{ background: BRAND, height: '2mm' }} />
    </div>
  );
}

// Print sheet holding one admit card per student, one per A4 page.
export function AdmitCardSheet({ students, school, datesheet, onClose }) {
  return (
    <PrintSheet title={`Admit cards (${students.length})`} onClose={onClose}>
      <div className="flex flex-col gap-6">
        {students.map(s => (
          <div key={s.id} className="admit-card-page">
            <AdmitCard student={s} school={school} datesheet={datesheet} />
          </div>
        ))}
      </div>
    </PrintSheet>
  );
}
